"use client"

import Link from "next/link"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { useCountry } from "@/components/layout/country-provider"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { addressWithCountry } from "@/lib/location-country-label"
import {
  cmsLocale,
  type CmsClinicHours,
  type ResolvedClinicService,
} from "@/lib/cms"
import type { ResolvedParkingSection } from "@/lib/locations"
import { needsMorningDraw } from "@/lib/morning-draw"
import { useEditRegion } from "@/lib/edit/edit-region-registry"
import { EditableField } from "@/components/editor/editable-field"
import {
  MapPinIcon,
  ClockIcon,
  CheckCircleOutlineIcon,
  ArrowUpRightIcon,
} from "@/components/ui/icons"
import { cn } from "@/lib/utils"

// ─── Shared per-clinic sections ───────────────────────────
//
// The rich per-clinic content blocks (address + hours, available services,
// parking, public transport) are rendered by BOTH the clinic detail page and
// the deep city overview page. They live here so the two surfaces stay in
// lockstep — one source of truth. Each section keeps its `{clinicId}-*` anchor
// (unique per clinic, so it's safe to render several on one city page) and its
// inline-editor regions.

/** The resolved per-clinic detail a section block needs. */
export interface ClinicDetailData {
  description: string
  hours: CmsClinicHours[] | null
  parking: ResolvedParkingSection[] | null
  publicTransport: string | null
  imagingServices: ResolvedClinicService[]
  bloodServices: ResolvedClinicService[]
  languageNote?: string
}

const DAY_KEY_MAP: Record<string, string> = {
  Monday: "cp_dayMon",
  Tuesday: "cp_dayTue",
  Wednesday: "cp_dayWed",
  Thursday: "cp_dayThu",
  Friday: "cp_dayFri",
  Saturday: "cp_daySat",
  Sunday: "cp_daySun",
}

/** Collapse consecutive same-time days into compact "Mon–Fri: 08:00–17:00" lines. */
export function compactHours(
  hours: CmsClinicHours[],
  t: (...args: never[]) => string
): string[] {
  const translate = t as unknown as (key: string) => string
  const groups: { days: string[]; time: string }[] = []
  hours.forEach((h) => {
    const last = groups[groups.length - 1]
    if (last && last.time === h.time) {
      last.days.push(h.day)
    } else {
      groups.push({ days: [h.day], time: h.time })
    }
  })
  return groups.map((g) => {
    const localDay = (d: string) =>
      DAY_KEY_MAP[d] ? translate(DAY_KEY_MAP[d]) : d.slice(0, 3)
    const days =
      g.days.length === 1
        ? localDay(g.days[0])
        : g.days.length === 5
          ? translate("cp_monFri")
          : g.days.map(localDay).join(", ")
    return `${days}: ${g.time}`
  })
}

// The public-transport prose splits into multiple `<p>` paragraphs, so the
// edit region wraps the container div (the field IS the whole block). The
// `publicTransport` field literal lives here, not at the call site, so the
// stamp-coverage lint can read it statically.
export function TextBlock({
  text,
  className,
  clinicId,
  editLocale,
}: {
  text: string
  className?: string
  /** Payload doc id — registers the inline-editor region. Absent → no region. */
  clinicId?: string
  /** CMS locale these docs were fetched in (region provenance). */
  editLocale: string
}) {
  const paragraphs = text.split("\n\n")
  const ref = useEditRegion<HTMLDivElement>(
    clinicId
      ? {
          collection: "locations",
          id: clinicId,
          field: "publicTransport",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )
  return (
    <div className={className} ref={ref}>
      {paragraphs.map((p, i) => (
        <p key={i} className={i > 0 ? "mt-4" : ""}>
          {p}
        </p>
      ))}
    </div>
  )
}

// ─── Service row ──────────────────────────────────────────

export function ServiceRow({
  service,
  lp,
  addonLabel,
}: {
  service: ResolvedClinicService
  lp: (path: string) => string
  addonLabel: string
}) {
  const content = (
    <div className="flex items-center gap-3">
      <CheckCircleOutlineIcon
        size={20}
        className="text-light-green-500 flex-shrink-0"
      />
      <span className="text-foreground text-base font-medium">
        {service.name}
      </span>
      {service.addon && (
        <span className="text-sm text-neutral-400">{addonLabel}</span>
      )}
    </div>
  )

  if (service.path) {
    return (
      <Link
        href={lp(service.path)}
        className="block transition-opacity hover:opacity-70"
      >
        {content}
      </Link>
    )
  }
  return content
}

// ─── Address + hours ──────────────────────────────────────

export function ClinicAddressHours({
  clinicId,
  address,
  hours,
  className,
}: {
  clinicId: string
  address: string
  hours: CmsClinicHours[] | null
  className?: string
}) {
  const t = useTranslations("locations")
  const locale = useLocale()
  const country = useCountry().code
  const fullAddress = addressWithCountry(address, country, locale)
  return (
    <section
      id={`${clinicId}-address`}
      className={cn("scroll-mt-32", className)}
    >
      <div className="relative flex flex-col gap-3 pl-5 before:absolute before:top-0 before:bottom-0 before:left-0 before:w-[3px] before:rounded-full before:bg-neutral-200">
        <div className="flex items-start gap-2.5">
          <MapPinIcon
            width={16}
            height={16}
            className="mt-1 flex-shrink-0 text-neutral-400"
          />
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
              fullAddress
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-start gap-1 text-base text-neutral-700 no-underline underline-offset-4 hover:underline hover:decoration-neutral-200 hover:decoration-[1px]"
          >
            <span>{fullAddress}</span>
            <ArrowUpRightIcon
              size={14}
              className="mt-1 flex-shrink-0 text-neutral-400"
            />
          </a>
        </div>
        {hours && (
          <div className="flex items-start gap-2.5">
            <ClockIcon
              width={16}
              height={16}
              className="mt-1 flex-shrink-0 text-neutral-400"
            />
            <div className="text-base text-neutral-700">
              {compactHours(hours, t).map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

// ─── Available services ───────────────────────────────────

export function ClinicServices({
  clinicId,
  clinicType,
  imagingServices,
  bloodServices,
  className,
}: {
  clinicId: string
  clinicType: "mri" | "blood"
  imagingServices: ResolvedClinicService[]
  bloodServices: ResolvedClinicService[]
  className?: string
}) {
  const t = useTranslations("locations")
  const lp = useLocalePath()
  if (imagingServices.length === 0 && bloodServices.length === 0) return null
  return (
    <section
      id={`${clinicId}-availability`}
      className={cn("scroll-mt-32", className)}
    >
      <h2 className="text-foreground text-2xl font-normal">
        {t("cp_availableAtLocation")}
      </h2>
      {imagingServices.length > 0 && bloodServices.length > 0 && (
        <p className="mt-2 text-base font-medium text-neutral-700">
          {clinicType === "mri"
            ? t("cp_servicesAvailMri")
            : t("cp_servicesAvailBlood")}
        </p>
      )}
      <div className="mt-10 grid grid-cols-1 gap-x-12 gap-y-6 md:grid-cols-2">
        {imagingServices.length > 0 && (
          <div>
            <p className="type-body-md text-foreground mb-3 font-medium">
              {t("cp_imaging")}
            </p>
            <div className="space-y-4">
              {imagingServices.map((s) => (
                <ServiceRow
                  key={s.name}
                  service={s}
                  lp={lp}
                  addonLabel={t("cp_addon")}
                />
              ))}
            </div>
          </div>
        )}
        {bloodServices.length > 0 && (
          <div>
            <p className="type-body-md text-foreground mb-3 font-medium">
              {t("cp_bloodTestsLabel")}
            </p>
            <div className="space-y-4">
              {bloodServices.map((s) => (
                <ServiceRow
                  key={s.name}
                  service={s}
                  lp={lp}
                  addonLabel={t("cp_addon")}
                />
              ))}
            </div>
            {needsMorningDraw(bloodServices) && (
              <p className="type-body-md mt-4 text-neutral-600">
                {t("cp_morningDrawNote")}
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

// ─── Parking ──────────────────────────────────────────────

export function ClinicParking({
  clinicId,
  parking,
  className,
}: {
  clinicId: string
  parking: ResolvedParkingSection[] | null
  className?: string
}) {
  const t = useTranslations("locations")
  if (!parking || parking.length === 0) return null
  return (
    <section
      id={`${clinicId}-parking`}
      className={cn("scroll-mt-32", className)}
    >
      <h2 className="text-foreground text-2xl font-normal">
        {t("cp_parkingInfo")}
      </h2>
      <div className="mt-4 max-w-3xl space-y-4">
        {parking.map((section, i) => (
          <div key={i}>
            {section.heading && (
              <h4 className="text-foreground text-base font-medium">
                {section.heading}
              </h4>
            )}
            {section.body && (
              <p className="text-base leading-relaxed text-neutral-700">
                {section.body}
              </p>
            )}
            {section.bullets && section.bullets.length > 0 && (
              <ul className="mt-1 ml-5 list-disc space-y-0.5">
                {section.bullets.map((bullet, j) => (
                  <li
                    key={j}
                    className="text-base leading-relaxed text-neutral-700"
                  >
                    {bullet}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}

// ─── Public transport ─────────────────────────────────────

export function ClinicTransport({
  clinicId,
  publicTransport,
  className,
}: {
  clinicId: string
  publicTransport: string | null
  className?: string
}) {
  const t = useTranslations("locations")
  const locale = useLocale()
  const country = useCountry().code
  if (!publicTransport) return null
  return (
    <section
      id={`${clinicId}-transport`}
      className={cn("scroll-mt-32", className)}
    >
      <h2 className="text-foreground text-2xl font-normal">
        {t("cp_publicTransport")}
      </h2>
      <TextBlock
        text={publicTransport}
        className="text-foreground mt-4 max-w-3xl text-base leading-relaxed"
        clinicId={clinicId}
        editLocale={cmsLocale(locale, country)}
      />
    </section>
  )
}

// ─── Language note ────────────────────────────────────────
// Highlighted note when a clinic serves only certain languages.

export function ClinicLanguageNote({
  clinicId,
  note,
  className,
}: {
  clinicId: string
  note: string | null | undefined
  className?: string
}) {
  const locale = useLocale()
  const country = useCountry().code
  if (!note) return null
  return (
    <div
      className={cn(
        "rounded-xl border border-amber-200 bg-amber-50 px-4 py-3",
        className
      )}
    >
      <EditableField
        collection="locations"
        id={clinicId}
        field="languageNote"
        locale={cmsLocale(locale, country)}
        kind="scalar"
        as="p"
        className="text-sm text-amber-800"
      >
        {note}
      </EditableField>
    </div>
  )
}
